import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Plus, Trash2, Edit2, X, Check, Loader2, Video, BookOpen, Newspaper, FlaskConical, ExternalLink, Upload, Image as ImageIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveImg = (url) => (!url ? "" : url.startsWith("http") ? url : `${BACKEND}${url}`);

const CONTENT_CONFIG = {
  "product-demos": { title: "Product Demo Videos", icon: Video, fields: ["title", "media_url", "product_code"] },
  "lab-info": { title: "Lab Info Updates", icon: FlaskConical, fields: ["title", "content", "link"] },
  "training": { title: "Training & Resources", icon: BookOpen, fields: ["title", "media_url"] },
  "news-events": { title: "News & Events", icon: Newspaper, fields: ["kind", "title", "content", "image_upload", "link"] },
};

export default function AdminContentManager() {
  const { type } = useParams();
  const navigate = useNavigate();
  const config = CONTENT_CONFIG[type];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", media_url: "", link: "", product_code: "", kind: "news", is_active: true });

  useEffect(() => {
    if (!config) { navigate("/admin"); return; }
    loadItems();
  }, [type]);

  const loadItems = async () => {
    setLoading(true);
    try { const { data } = await API.get(`/admin/content/${type}`); setItems(data || []); }
    catch {} finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await API.put(`/admin/content/${type}/${editing}`, form);
      } else {
        await API.post(`/admin/content/${type}`, form);
      }
      setShowForm(false); setEditing(null);
      setForm({ title: "", content: "", media_url: "", link: "", product_code: "", kind: "news", is_active: true });
      loadItems();
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await API.delete(`/admin/content/${type}/${id}`);
    loadItems();
  };

  const [uploadingImage, setUploadingImage] = useState(false);
  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await API.post("/admin/assets/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data?.url) setForm(f => ({ ...f, media_url: res.data.url }));
    } catch (e) {
      window.alert(e.response?.data?.detail || "Image upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const startEdit = (item) => {
    setForm({ title: item.title || "", content: item.content || "", media_url: item.media_url || "", link: item.link || "", product_code: item.product_code || "", kind: item.kind || "news", is_active: item.is_active !== false });
    setEditing(item.id);
    setShowForm(true);
  };

  if (!config) return null;
  const Icon = config.icon;

  return (
    <div className="space-y-6" data-testid={`admin-content-${type}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className="w-6 h-6 text-[#008A99]" />
          <div><h1 className="text-2xl font-bold text-gray-900">{config.title}</h1><p className="text-sm text-gray-500">Manage content visible on the mobile app</p></div>
        </div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ title: "", content: "", media_url: "", link: "", product_code: "", kind: "news", is_active: true }); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#008A99] text-white rounded-xl text-sm font-semibold" data-testid="add-content-btn">
          <Plus className="w-4 h-4" /> Add New
        </button>
      </div>

      {showForm && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="content-form">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">{editing ? "Edit" : "Add"} {config.title.replace(/s$/, "")}</h3>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
          </div>
          {config.fields.includes("kind") && (
            <div data-testid="content-kind-wrapper">
              <label className="text-xs font-semibold text-gray-500 uppercase">Type *</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {["news", "event"].map(k => (
                  <button key={k} type="button" onClick={() => setForm({ ...form, kind: k })}
                    className={`px-3 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${form.kind === k ? "bg-[#008A99] text-white border-[#008A99]" : "bg-white text-gray-600 border-gray-200 hover:border-[#008A99]"}`}
                    data-testid={`content-kind-${k}`}>
                    {k === "news" ? "News" : "Event"}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase">Title *</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Enter title"
              className="mt-1 w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="content-title-input" />
          </div>
          {config.fields.includes("content") && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Content / Description</label>
              <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={3} placeholder="Enter content..."
                className="mt-1 w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-[#008A99] resize-none" data-testid="content-body-input" />
            </div>
          )}
          {config.fields.includes("media_url") && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Media URL (Video/Image link)</label>
              <input type="url" value={form.media_url} onChange={e => setForm({ ...form, media_url: e.target.value })} placeholder="https://..."
                className="mt-1 w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="content-media-input" />
            </div>
          )}
          {config.fields.includes("image_upload") && (
            <div data-testid="content-image-upload-wrapper">
              <label className="text-xs font-semibold text-gray-500 uppercase">Article Image / Banner</label>
              <p className="text-[10px] text-gray-400 mb-1">Upload the "Expert Speak"-style image (or quote graphic) shown on top of the article card.</p>
              {form.media_url ? (
                <div className="flex items-start gap-3 p-2 border border-emerald-200 bg-emerald-50/40 rounded-xl">
                  <img src={resolveImg(form.media_url)} alt="preview" className="w-24 h-24 object-cover rounded-lg" data-testid="content-image-preview" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-emerald-700 font-mono truncate">{form.media_url}</p>
                    <button type="button" onClick={() => setForm(f => ({ ...f, media_url: "" }))} className="mt-1 text-[10px] font-bold text-red-600 hover:underline" data-testid="content-image-remove">Remove</button>
                  </div>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 py-4 border border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:bg-gray-50 cursor-pointer" data-testid="content-image-upload-label">
                  {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploadingImage ? "Uploading…" : "Upload image"}
                  <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e.target.files?.[0])} data-testid="content-image-upload-input" />
                </label>
              )}
            </div>
          )}
          {config.fields.includes("link") && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Resource Link</label>
              <input type="url" value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} placeholder="https://..."
                className="mt-1 w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="content-link-input" />
            </div>
          )}
          {config.fields.includes("product_code") && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">Product Code</label>
              <input type="text" value={form.product_code} onChange={e => setForm({ ...form, product_code: e.target.value })} placeholder="e.g. 120247"
                className="mt-1 w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-[#008A99]" data-testid="content-product-input" />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="accent-[#008A99]" />
            Active (visible on app)
          </label>
          <button onClick={handleSave} className="px-6 py-2.5 bg-[#008A99] text-white rounded-xl text-sm font-semibold flex items-center gap-2" data-testid="save-content-btn">
            <Check className="w-4 h-4" /> Save
          </button>
        </div>
      )}

      {loading ? <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" /></div> : (
        <div className="space-y-3">
          {items.length === 0 ? <p className="text-center text-gray-400 py-8 text-sm">No items yet. Click "Add New" to create one.</p> :
            items.map(item => (
              <div key={item.id} className="bg-white border rounded-xl p-4 flex items-start justify-between gap-4" data-testid="content-item">
                {item.media_url && /\.(png|jpe?g|webp|gif|svg|avif)(\?|$)/i.test(item.media_url) && (
                  <img src={resolveImg(item.media_url)} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0 border border-slate-100" data-testid={`content-thumb-${item.id}`} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {item.kind && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${item.kind === "event" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`} data-testid={`content-kind-badge-${item.kind}`}>
                        {item.kind}
                      </span>
                    )}
                    <h3 className="font-semibold text-gray-900 truncate">{item.title}</h3>
                    {!item.is_active && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">Hidden</span>}
                  </div>
                  {item.content && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.content}</p>}
                  {(item.media_url || item.link) && (
                    <a href={item.media_url || item.link} target="_blank" rel="noopener noreferrer" className="text-xs text-[#008A99] flex items-center gap-1 mt-1">
                      <ExternalLink className="w-3 h-3" /> {item.media_url || item.link}
                    </a>
                  )}
                  <p className="text-[10px] text-gray-400 mt-1">{new Date(item.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button onClick={() => startEdit(item)} className="p-2 hover:bg-gray-100 rounded-lg"><Edit2 className="w-4 h-4 text-gray-400" /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}
